import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { tripService } from 'services/tripService';
import { userService } from 'services/userService';
import { tripSummaryService } from '../../../services/tripSummaryService';

const TripHeader = ({ trip, onEdit, onShare, onDuplicate, onGeneratePDF, onTitleUpdate }) => {
  const { user } = useAuth();
  const [isPublic, setIsPublic] = useState(false);
  const [effectiveVisibility, setEffectiveVisibility] = useState('private');
  const [saving, setSaving] = useState(false);
  const isOwner = user?.id === trip?.userId;

  useEffect(() => {
    loadEffectiveVisibility();
  }, [trip?.id, user?.id]);

  const loadEffectiveVisibility = async () => {
    if (!trip?.id || !user?.id) return;
    
    try {
      // Get user's default visibility
      const defaultVisibility = await userService?.getDefaultTripVisibility(user?.id);
      
      // Calculate effective visibility
      const effective = trip?.visibilityOverride || defaultVisibility;
      setEffectiveVisibility(effective);
      setIsPublic(effective === 'public');
    } catch (error) {
      console.error('Error loading visibility:', error);
    }
  };

  const handleVisibilityToggle = async () => {
    if (!isOwner || saving) return;

    try {
      setSaving(true);
      const newVisibility = isPublic ? 'private' : 'public';
      
      await tripService?.updateTripVisibilityOverride(trip?.id, newVisibility);
      
      setIsPublic(!isPublic);
      setEffectiveVisibility(newVisibility);
    } catch (error) {
      console.error('Error updating visibility:', error);
      alert('Failed to update trip visibility');
    } finally {
      setSaving(false);
    }
  };

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(trip?.title || '');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  const handleTitleEdit = () => {
    setIsEditingTitle(true);
    setEditedTitle(trip?.title || '');
  };

  const handleTitleSave = async () => {
    if (!editedTitle?.trim()) {
      // Don't allow empty titles
      setEditedTitle(trip?.title || '');
      setIsEditingTitle(false);
      return;
    }

    if (editedTitle === trip?.title) {
      // No changes, just exit edit mode
      setIsEditingTitle(false);
      return;
    }

    setIsSavingTitle(true);
    try {
      await onTitleUpdate?.(editedTitle?.trim());
      setIsEditingTitle(false);
    } catch (error) {
      console.error('Failed to update title:', error);
      // Revert to original title on error
      setEditedTitle(trip?.title || '');
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleTitleCancel = () => {
    setEditedTitle(trip?.title || '');
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e?.key === 'Enter') {
      handleTitleSave();
    } else if (e?.key === 'Escape') {
      handleTitleCancel();
    }
  };

  // ✅ NEW: Add summary editing state
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState(trip?.summary || '');
  const [isSavingSummary, setIsSavingSummary] = useState(false);
  const [isRegeneratingSummary, setIsRegeneratingSummary] = useState(false);

  // ✅ NEW: Initialize summary when trip loads
  useEffect(() => {
    if (trip?.summary) {
      setEditedSummary(trip?.summary);
    } else {
      // Auto-generate summary if it doesn't exist
      handleGenerateSummary();
    }
  }, [trip?.id]);

  // ✅ NEW: Handle summary editing
  const handleSummaryEdit = () => {
    setIsEditingSummary(true);
    setEditedSummary(trip?.summary || '');
  };

  // ✅ NEW: Handle summary save
  const handleSummarySave = async () => {
    if (!editedSummary?.trim()) {
      // Don't allow empty summaries - regenerate instead
      await handleGenerateSummary();
      setIsEditingSummary(false);
      return;
    }

    if (editedSummary === trip?.summary) {
      // No changes, just exit edit mode
      setIsEditingSummary(false);
      return;
    }

    setIsSavingSummary(true);
    try {
      await tripService?.updateTripSummary?.(trip?.id, editedSummary?.trim());
      setIsEditingSummary(false);
      // Trigger parent component refresh if callback exists
      if (onTitleUpdate) {
        // Reuse onTitleUpdate callback to trigger refresh
        await onTitleUpdate(trip?.title);
      }
    } catch (error) {
      console.error('Failed to update summary:', error);
      // Revert to original summary on error
      setEditedSummary(trip?.summary || '');
    } finally {
      setIsSavingSummary(false);
    }
  };

  // ✅ NEW: Handle summary cancel
  const handleSummaryCancel = () => {
    setEditedSummary(trip?.summary || '');
    setIsEditingSummary(false);
  };

  // ✅ NEW: Handle summary regeneration
  const handleGenerateSummary = async () => {
    setIsRegeneratingSummary(true);
    try {
      const summary = await tripSummaryService?.generateTripSummary(trip);
      setEditedSummary(summary);
      
      // Auto-save the generated summary
      await tripService?.updateTripSummary?.(trip?.id, summary);
      
      // Trigger parent component refresh
      if (onTitleUpdate) {
        await onTitleUpdate(trip?.title);
      }
    } catch (error) {
      console.error('Failed to generate summary:', error);
      // Use default summary if generation fails
      const defaultSummary = tripSummaryService?.generateDefaultSummary(trip);
      setEditedSummary(defaultSummary);
    } finally {
      setIsRegeneratingSummary(false);
    }
  };

  // ✅ NEW: Handle summary keydown events
  const handleSummaryKeyDown = (e) => {
    if (e?.key === 'Escape') {
      handleSummaryCancel();
    }
    // Don't handle Enter for textarea (allow line breaks)
  };

  // ✅ NEW: Add effect to recalculate duration when trip data changes
  useEffect(() => {
    if (trip?.duration) {
      // Force component to recognize duration updates
      setEditedTitle(trip?.title || '');
    }
  }, [trip?.duration, trip?.startDate, trip?.endDate]);

  // ✅ UPDATED: Calculate duration with proper dependency on trip data
  const calculateDurationDetails = () => {
    // Use trip?.duration directly from database (already calculated server-side)
    const days = trip?.duration || 0;
    const nights = days > 0 ? days - 1 : 0;
    
    if (days === 0) return null;
    if (days === 1) return '1 Day';
    if (nights === 0) return `${days} Days`;
    return `${days} Days, ${nights} ${nights === 1 ? 'Night' : 'Nights'}`;
  };

  // ✅ CRITICAL FIX: Memoize duration text to ensure it updates when trip.duration changes
  const durationText = useMemo(() => calculateDurationDetails(), [trip?.duration]);

  return (
    <div className="bg-card rounded-organic shadow-organic p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary rounded-organic flex items-center justify-center">
              <Icon name="MapPin" size={24} color="white" />
            </div>
            <div className="flex-1">
              {isEditingTitle ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e?.target?.value)}
                    onKeyDown={handleTitleKeyDown}
                    onBlur={handleTitleSave}
                    autoFocus
                    disabled={isSavingTitle}
                    className="font-heading font-heading-bold text-2xl lg:text-3xl"
                    placeholder="Enter trip title"
                  />
                  {isSavingTitle ? (
                    <Icon name="Loader" size={20} className="text-primary animate-spin" />
                  ) : (
                    <div className="flex gap-1">
                      <button
                        onClick={handleTitleSave}
                        className="p-1 hover:bg-success/10 rounded-organic transition-colors"
                        title="Save title"
                      >
                        <Icon name="Check" size={20} className="text-success" />
                      </button>
                      <button
                        onClick={handleTitleCancel}
                        className="p-1 hover:bg-destructive/10 rounded-organic transition-colors"
                        title="Cancel"
                      >
                        <Icon name="X" size={20} className="text-destructive" />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 group">
                  <h1 className="font-heading font-heading-bold text-2xl lg:text-3xl text-foreground">
                    {trip?.title}
                  </h1>
                  <button
                    onClick={handleTitleEdit}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-primary/10 rounded-organic transition-all"
                    title="Edit trip title"
                  >
                    <Icon name="Pencil" size={18} className="text-primary" />
                  </button>
                </div>
              )}
              <p className="font-body text-muted-foreground">
                {trip?.destination}
                {(trip?.city || trip?.state) && (
                  <span className="ml-2 text-primary font-medium">
                    📍 {trip?.city && trip?.state 
                      ? `${trip?.city}, ${trip?.state}` 
                      : trip?.state || trip?.city
                    }
                  </span>
                )}
              </p>
              {/* Duration Display */}
              {durationText && (
                <div className="flex items-center gap-2 mt-1">
                  <Icon name="Calendar" size={16} className="text-muted-foreground" />
                  <span className="font-body text-sm text-muted-foreground">
                    {durationText}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            iconPosition="left"
            onClick={onEdit}
          >
            Edit Trip
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Share"
            iconPosition="left"
            onClick={onShare}
          >
            Share
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Copy"
            iconPosition="left"
            onClick={onDuplicate}
          >
            Duplicate
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="Download"
            iconPosition="left"
            onClick={onGeneratePDF}
          >
            Download PDF
          </Button>
        </div>
      </div>

      {/* ✅ Trip Summary Section - Always show */}
      <div className="mt-4 p-4 bg-muted rounded-organic-sm">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Trip Summary
          </h3>
          {isOwner && !isEditingSummary && !isRegeneratingSummary && (
            <div className="flex gap-1">
              <button
                onClick={handleSummaryEdit}
                className="p-1 hover:bg-primary/10 rounded-organic transition-colors"
                title="Edit summary"
              >
                <Icon name="Pencil" size={16} className="text-primary" />
              </button>
              <button
                onClick={handleGenerateSummary}
                className="p-1 hover:bg-primary/10 rounded-organic transition-colors"
                title="Regenerate summary with AI"
              >
                <Icon name="RefreshCw" size={16} className="text-primary" />
              </button>
            </div>
          )}
        </div>

        {isRegeneratingSummary ? (
          <div className="flex items-center gap-2 py-2">
            <Icon name="Loader" size={20} className="text-primary animate-spin" />
            <p className="font-body text-sm text-muted-foreground">
              Generating an exciting summary for your trip...
            </p>
          </div>
        ) : isEditingSummary ? (
          <div className="space-y-2">
            <textarea
              value={editedSummary}
              onChange={(e) => setEditedSummary(e?.target?.value)}
              onKeyDown={handleSummaryKeyDown}
              disabled={isSavingSummary}
              rows={3}
              className="w-full px-3 py-2 font-body text-foreground bg-background border border-border rounded-organic focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Enter a 2-3 sentence summary of your trip..."
              autoFocus
            />
            <div className="flex items-center gap-2">
              {isSavingSummary ? (
                <Icon name="Loader" size={20} className="text-primary animate-spin" />
              ) : (
                <>
                  <Button
                    size="sm"
                    iconName="Check"
                    iconPosition="left"
                    onClick={handleSummarySave}
                  >
                    Save Summary
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="X"
                    iconPosition="left"
                    onClick={handleSummaryCancel}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <p className="font-body text-foreground leading-relaxed">
            {trip?.summary || editedSummary || 'No summary available yet. Click the refresh icon to generate one!'}
          </p>
        )}
      </div>
    </div>
  );
};

export default TripHeader;